import { atom, type ExtractAtomValue, useAtomValue, useSetAtom } from 'jotai';
import * as React from 'react';

import type * as api from '@gitbook/api-client';
import { confirm, intlPluralizeString, toast } from '@gitbook/spine-react';
import { useEventCallback } from '@gitbook/util-react-hooks';

import { apiClient, type SiteStructureContent } from '../../../state';
import { SiteStructureItemList } from './SiteStructureItemList';

export interface UseDeleteSiteItemsProps {
    organization: api.Organization;
    site: api.Site;
}

export function useDeleteSiteItems(props: UseDeleteSiteItemsProps) {
    const { organization, site } = props;
    const setDeletedSiteItems = useSetAtom(deletedSiteItemsAtom);

    return useEventCallback((items: SiteStructureContent[]) => {
        const siteSectionGroups = items.filter(
            (item): item is api.SiteSectionGroup => item.object === 'site-section-group'
        );
        const siteSections = items.filter(
            (item): item is api.SiteSection => item.object === 'site-section'
        );
        const siteSpaces = items.filter(
            (item): item is api.SiteSpace => item.object === 'site-space'
        );
        const siteExternalLinks = items.filter(
            (item): item is api.SiteExternalLink => item.object === 'site-external-link'
        );

        function getTitleAndDescription() {
            if (
                siteSectionGroups.length > 0 &&
                siteSections.length === 0 &&
                siteSpaces.length === 0 &&
                siteExternalLinks.length === 0
            ) {
                const pluralizedGroup = intlPluralizeString({
                    count: siteSectionGroups.length,
                    singular: 'group',
                    plural: 'groups',
                });
                return {
                    title: `Remove ${pluralizedGroup}?`,
                    description: `This will remove the ${pluralizedGroup} but any sections will remain in the site.`,
                    successToast: `The ${pluralizedGroup} have been removed`,
                    failureToast: `Error while removing ${pluralizedGroup}`,
                };
            }
            if (
                siteSectionGroups.length === 0 &&
                siteSections.length > 0 &&
                siteSpaces.length === 0 &&
                siteExternalLinks.length === 0
            ) {
                const pluralizedSection = intlPluralizeString({
                    count: siteSections.length,
                    singular: 'section',
                    plural: 'sections',
                });
                return {
                    title: `Remove ${pluralizedSection}?`,
                    description: `The ${pluralizedSection} and its variants will be removed from this site.`,
                    successToast: `The ${pluralizedSection} have been removed`,
                    failureToast: `Error while removing ${pluralizedSection}`,
                };
            }
            if (
                siteSectionGroups.length === 0 &&
                siteSections.length === 0 &&
                siteSpaces.length > 0 &&
                siteExternalLinks.length === 0
            ) {
                const pluralizedVariant = intlPluralizeString({
                    count: siteSpaces.length,
                    singular: 'variant',
                    plural: 'variants',
                });
                return {
                    title: `Remove ${pluralizedVariant}?`,
                    description: `The ${pluralizedVariant} will be removed from this site.`,
                    successToast: `The ${pluralizedVariant} have been removed`,
                    failureToast: `Error while removing ${pluralizedVariant}`,
                };
            }

            if (
                siteSectionGroups.length === 0 &&
                siteSections.length === 0 &&
                siteSpaces.length === 0 &&
                siteExternalLinks.length > 0
            ) {
                const pluralizedLink = intlPluralizeString({
                    count: siteExternalLinks.length,
                    singular: 'external link',
                    plural: 'external links',
                });
                return {
                    title: `Remove ${pluralizedLink}?`,
                    description: `The ${pluralizedLink} will be removed from this site.`,
                    successToast: `The ${pluralizedLink} have been removed`,
                    failureToast: `Error while removing ${pluralizedLink}`,
                };
            }

            const total =
                siteSectionGroups.length +
                siteSections.length +
                siteSpaces.length +
                siteExternalLinks.length;

            const pluralizedItems = intlPluralizeString({
                count: total,
                singular: 'item',
                plural: 'items',
            });

            return {
                title: `Remove ${pluralizedItems}?`,
                description: `The ${pluralizedItems} will be removed from this site.`,
                successToast: `The ${pluralizedItems} have been removed`,
                failureToast: `Error while removing ${pluralizedItems}`,
            };
        }

        const { title, description, successToast, failureToast } = getTitleAndDescription();

        void confirm({
            title,
            description,
            confirmLabel: 'Remove',
            children: <SiteStructureItemList items={items} />,
            kind: 'danger',
            onConfirm: async (_event, { preventClose }) => {
                await toast.catch(
                    async () => {
                        const deletedSiteItems = {
                            organizationId: organization.id,
                            siteId: site.id,
                            items,
                        };
                        setDeletedSiteItems(deletedSiteItems);

                        try {
                            await Promise.all(
                                siteExternalLinks.map((siteExternalLink) =>
                                    apiClient.siteExternalLinks.deleteSiteExternalLinkById(
                                        organization.id,
                                        site.id,
                                        siteExternalLink.id
                                    )
                                )
                            );
                            await Promise.all(
                                siteSpaces.map((siteSpace) =>
                                    apiClient.siteSpaces.deleteSiteSpaceById(
                                        organization.id,
                                        site.id,
                                        siteSpace.id
                                    )
                                )
                            );
                            await Promise.all(
                                siteSections.map((section) =>
                                    apiClient.siteSections.deleteSiteSectionById(
                                        organization.id,
                                        site.id,
                                        section.id
                                    )
                                )
                            );
                            await Promise.all(
                                siteSectionGroups.map((group) =>
                                    apiClient.siteSectionGroups.deleteSiteSectionGroupById(
                                        organization.id,
                                        site.id,
                                        group.id
                                    )
                                )
                            );
                        } catch (error) {
                            setDeletedSiteItems((current) =>
                                current === deletedSiteItems ? null : current
                            );
                            throw error;
                        }
                        toast.success(successToast);
                    },
                    failureToast,
                    { onReject: preventClose }
                );
            },
        });
        return;
    });
}

const deletedSiteItemsAtom = atom<{
    organizationId: string;
    siteId: string;
    items: SiteStructureContent[];
} | null>(null);

/**
 * Return the deleted site item that previously contained the open space.
 * This lets the app keep the site context while displaying the deleted-item screen.
 */
export function useDeletedSiteItem(
    context: { organizationId: string; siteId: string; spaceId: string } | null
): api.SiteSection | api.SiteSpace | null {
    const deletedSiteItems = useAtomValue(deletedSiteItemsAtom);

    return context ? getDeletedSiteContentItem(deletedSiteItems, context) : null;
}

function getDeletedSiteContentItem(
    deleted: ExtractAtomValue<typeof deletedSiteItemsAtom>,
    context: { organizationId: string; siteId: string; spaceId: string }
): api.SiteSection | api.SiteSpace | null {
    if (
        !deleted ||
        deleted.organizationId !== context.organizationId ||
        deleted.siteId !== context.siteId
    ) {
        return null;
    }

    const section = deleted.items.find(
        (item): item is api.SiteSection =>
            item.object === 'site-section' &&
            item.siteSpaces.some((siteSpace) => siteSpace.space.id === context.spaceId)
    );
    if (section) {
        return section;
    }

    const variant = deleted.items.find(
        (item): item is api.SiteSpace =>
            item.object === 'site-space' && item.space.id === context.spaceId
    );
    return variant ?? null;
}
